import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";

const Users = () => {
  const qc = useQueryClient();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [creating, setCreating] = useState(false);

  const { data: admins } = useQuery({
    queryKey: ["admin_users"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("user_roles")
        .select("user_id, created_at")
        .eq("role", "admin");
      if (error) throw error;
      return data;
    },
  });

  const handleCreate = async (e: React.FormEvent) => {
    e.preventDefault();
    setCreating(true);
    try {
      // Create user via signUp (auto-confirmed). Then grant admin role.
      const { data: signUpData, error: signUpErr } = await supabase.auth.signUp({
        email,
        password,
        options: { emailRedirectTo: `${window.location.origin}/admin` },
      });
      if (signUpErr) throw signUpErr;
      const newUserId = signUpData.user?.id;
      if (newUserId) {
        const { error: roleErr } = await supabase
          .from("user_roles")
          .insert({ user_id: newUserId, role: "admin" });
        if (roleErr && !roleErr.message.includes("duplicate")) throw roleErr;
      }
      toast.success(`Käyttäjä ${email} lisätty. Anna sähköposti ja salasana hänelle.`);
      setEmail("");
      setPassword("");
      qc.invalidateQueries({ queryKey: ["admin_users"] });
    } catch (err: any) {
      toast.error(err.message || "Käyttäjän lisäys epäonnistui");
    } finally {
      setCreating(false);
    }
  };

  return (
    <div className="space-y-8">
      <div>
        <h2 className="mb-6 font-display text-2xl font-semibold">Käyttäjät</h2>
        <div className="rounded-xl border bg-card p-6">
          <p className="text-sm text-muted-foreground">
            Tällä hetkellä admin-oikeudet on <strong>{admins?.length ?? 0}</strong> käyttäjällä.
          </p>
        </div>
      </div>

      <div>
        <h3 className="mb-4 font-display text-lg font-semibold">Lisää uusi admin-käyttäjä</h3>
        <form onSubmit={handleCreate} className="space-y-4 rounded-xl border bg-card p-6">
          <div>
            <Label htmlFor="new-email">Sähköposti</Label>
            <Input id="new-email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} required />
          </div>
          <div>
            <Label htmlFor="new-password">Tilapäinen salasana (kerro hänelle)</Label>
            <Input id="new-password" type="text" value={password} onChange={(e) => setPassword(e.target.value)} required minLength={8} />
            <p className="mt-1 text-xs text-muted-foreground">
              Vähintään 8 merkkiä. Käyttäjä voi vaihtaa sen myöhemmin.
            </p>
          </div>
          <Button type="submit" disabled={creating}>
            {creating ? "Lisätään..." : "Lisää käyttäjä"}
          </Button>
          <p className="rounded-md bg-muted p-3 text-xs text-muted-foreground">
            ⚠️ Huom: tämä lisää käyttäjän käyttäen sinun selaintasi. Sinut kirjataan ulos hetkellisesti – kirjaudu takaisin sisään lisäyksen jälkeen.
          </p>
        </form>
      </div>
    </div>
  );
};

export default Users;
